"use client";

import type { RefObject } from "react";
import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";
import { FILTER_RESULTS_ID } from "../../hooks/useFilterResultsScroll";
import { REVIVE_CARD_INDEX, REVIVE_WIDE_INDEX } from "../../lifestyle-constants";
import PrizeCard from "../prizeCard/PrizeCard";
import LifestyleAdSlot from "./LifestyleAdSlot";

interface AllPrizesGridProps {
  /** The revealed slice; progressive reveal grows this as the user scrolls. */
  prizes: LifestylePrize[];
  /** Matching prizes in total, which is what the count above the grid reports. */
  totalCount: number;
  heading?: string;
  /**
   * Only the filtered view passes this. It both supplies the "back to lobby"
   * action and is what switches the header to the filtered form — the count
   * moves into the title, as in "RESULTS (0)".
   */
  onBackToLobby?: () => void;
  onAddToCart: (prize: LifestylePrize) => void;
  /** Revive ads are suppressed once a filter is applied. */
  showAds: boolean;
  hasMore: boolean;
  sentinelRef: RefObject<HTMLDivElement>;
}

/** The flat 4-column grid below the collection rows, and the filtered view. */
export default function AllPrizesGrid({
  prizes,
  totalCount,
  heading = "All prizes",
  onBackToLobby,
  onAddToCart,
  showAds,
  hasMore,
  sentinelRef,
}: AllPrizesGridProps) {
  const cells = prizes.flatMap((prize, index) => {
    const cell = (
      <div key={prize.ContentNodeId} className="h-full">
        <PrizeCard prize={prize} onAddToCart={onAddToCart} isPriority={index < 8} />
      </div>
    );

    if (!showAds) return [cell];

    // Same positions the Angular version used: a card-shaped ad in the second
    // slot and a wide banner after the second row.
    // LifestyleAdSlot stays `hidden` until Revive fills it, so an empty zone
    // does not reserve a grid cell (unlike a wrapper with `[&:empty]:hidden`,
    // which never collapses because it always contains the <ins> nodes).
    if (index === REVIVE_CARD_INDEX) {
      return [cell, <LifestyleAdSlot key="ad-card" variant="card" />];
    }

    if (index === REVIVE_WIDE_INDEX) {
      return [cell, <LifestyleAdSlot key="ad-wide" variant="wide" />];
    }

    return [cell];
  });

  return (
    // Same shell as CollectionRow: a full-width section whose children each
    // pair a padded wrapper with the page's 1320px content track, so the
    // heading and cards here line up with the carousels above.
    <section
      id={onBackToLobby ? FILTER_RESULTS_ID : undefined}
      className="flex flex-col w-full pt-4 lg:pt-6 pb-2 lg:pb-6 gap-3">
      <div className="w-full px-4 lg:px-5">
        <div className="flex items-center justify-between w-full max-w-[1320px] mx-auto gap-3">
          <h2 className="text-lg font-semibold leading-6 uppercase text-dark-1 text-left !mb-0">
            {onBackToLobby ? `${heading} (${totalCount})` : heading}
          </h2>
          {onBackToLobby ? (
            <button
              type="button"
              onClick={onBackToLobby}
              className="shrink-0 text-sm font-medium leading-5 uppercase text-blue hover:underline">
              Back to lobby
            </button>
          ) : (
            <span className="text-xs text-gray-5">
              {totalCount} {totalCount === 1 ? "prize" : "prizes"}
            </span>
          )}
        </div>
      </div>

      {totalCount === 0 ? (
        <div className="flex flex-col items-center w-full px-4 py-6">
          <img
            src="/assets/images/lifestyle/search/no-results.webp"
            alt=""
            aria-hidden="true"
            className="block size-[100px]"
          />
          <div className="flex flex-col items-center w-full pt-[30px] gap-[15px]">
            <div className="text-2xl leading-6 uppercase text-gray-1 text-center">No prizes found</div>
            {/* Capped rather than left to the 1320px track, so the centred
                copy still reads as a block on desktop. */}
            <p className="font-roboto text-lg leading-7 tracking-[-0.18px] text-[#9A9A9A] text-center">
              Nothing matches your search. Try a different word, or clear the search with the ✕ above.
            </p>
          </div>
        </div>
      ) : (
        // The 4px mobile inset is what makes two cards fit at their designed
        // 172px; the card's own padding still puts its text level with the
        // heading above, which keeps its full 16px gutter.
        <div className="w-full px-1 lg:px-5">
          {/* Four columns of the 1320px track land on the same 318px card the
              carousels draw, so the two layouts share a grid. */}
          {/* Dense flow lets prizes after the wide banner backfill any hole left
              when the card ad is missing, so the banner always sits under a
              complete row (same trick as the cars lobby grid). */}
          <div className="grid grid-flow-dense grid-cols-2 lg:grid-cols-4 w-full max-w-[1320px] mx-auto gap-2 lg:gap-4">
            {cells}
          </div>
        </div>
      )}

      {/* Watched by useProgressiveReveal to pull in the next batch. */}
      {hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-px" />}
    </section>
  );
}
